export type RGB = [number, number, number];

function hexToRgb(hex: string): RGB {
  const full = hex.replace('#', '');
  const norm = full.length === 3 ? full.split('').map((c) => c + c).join('') : full;
  const r = parseInt(norm.slice(0, 2), 16);
  const g = parseInt(norm.slice(2, 4), 16);
  const b = parseInt(norm.slice(4, 6), 16);
  return [r, g, b];
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export function interpolateStops(stops: string[], t: number): RGB {
  if (stops.length === 0) return [0, 0, 0];
  const clamped = Math.min(1, Math.max(0, t));
  if (stops.length === 1) return hexToRgb(stops[0]);
  const segments = stops.length - 1;
  const scaled = clamped * segments;
  const i = Math.min(segments - 1, Math.floor(scaled));
  const localT = scaled - i;
  const [r1, g1, b1] = hexToRgb(stops[i]);
  const [r2, g2, b2] = hexToRgb(stops[i + 1]);
  return [
    Math.round(lerp(r1, r2, localT)),
    Math.round(lerp(g1, g2, localT)),
    Math.round(lerp(b1, b2, localT)),
  ];
}

export interface CellColors {
  background: string;
  text: '#000000' | '#FFFFFF';
}

// Perceptual (sqrt) warp of the [min, max] range. A large max (e.g. precip 0..53)
// crushes every small value into the same near-white tone under a linear scale;
// sqrt spreads the low end so 0.9 reads distinctly from 0.0. A range that crosses
// zero is divergent data (anomaly) and stays LINEAR: the warp would drag the
// neutral 0 to ~0.71 of a sequential ramp, so the ramp midpoint would stop
// meaning "no anomaly". The legend ticks in HeatmapTable are the inverse of this
// (value at position p = min + p^2*(max-min), or p*(max-min) when linear);
// keep the two in sync if the warp ever changes.
export function scaleFraction(value: number, min: number, max: number): number {
  if (!(max > min)) return 0.5;
  const linear = (value - min) / (max - min);
  const clamped = Math.min(1, Math.max(0, linear));
  if (min < 0) return clamped;
  return Math.sqrt(clamped);
}

const BLACK: RGB = [0, 0, 0];
const WHITE: RGB = [255, 255, 255];

// Mesmas cores do `cellColors`, em números: para quem desenha fora do CSS (o PDF do heatmap).
export function cellRgb(
  value: number,
  min: number,
  max: number,
  stops: string[],
): { background: RGB; text: RGB } {
  const background = interpolateStops(stops, scaleFraction(value, min, max));
  const [r, g, b] = background;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return { background, text: luminance > 0.6 ? BLACK : WHITE };
}

export function cellColors(value: number, min: number, max: number, stops: string[]): CellColors {
  const { background: [r, g, b], text } = cellRgb(value, min, max, stops);
  return {
    background: `rgb(${r}, ${g}, ${b})`,
    text: text === BLACK ? '#000000' : '#FFFFFF',
  };
}

// Valores dos ticks da legenda, em posições igualmente espaçadas na barra: o inverso do
// `scaleFraction` (v = min + p^2 * (max - min) no warp sqrt; v = min + p * (max - min) no
// ramo linear/divergente). A tabela da tela e o PDF usam esta mesma régua.
export function legendTicks(min: number, max: number, count: number): number[] {
  if (!(max > min)) return [min];
  const warped = min >= 0;
  return Array.from({ length: count }, (_, i) => {
    const p = i / (count - 1);
    return min + (warped ? p * p : p) * (max - min);
  });
}

export const formatBound = (value: number): string => (
  Number.isInteger(value) ? `${value}` : value.toFixed(1)
);
